import { useRef } from "react";
import styled from "styled-components";
import greenLogo from "../../assets/greenLogo.svg";
import { lightTheme } from "../../styles/theme";
import { openInNewTab } from "../../utils/openInNewTab";
import { useTranslation } from "../../lang/TranslationContext";
import { AnimateIn } from "../AnimateIn";

export const Footer = () => {
  const ref = useRef<HTMLDivElement>(null);
  const { translations } = useTranslation();

  return (
    <AnimateIn noOpacity>
      <Wrap id="footer">
        <InnerWrap>
          <Text>
            <p>MarketEcho © 2025</p>
            <p
              onClick={() =>
                openInNewTab("https://marketecho.io/privacy_policy.pdf")
              }
            >
              {translations.landing.footer.privacy}
            </p>
            <p
              onClick={() =>
                openInNewTab("https://marketecho.io/terms_and_conditions.pdf")
              }
            >
              {translations.landing.footer.terms}
            </p>
            <p
              onClick={() =>
                openInNewTab("https://marketecho.io/disclaimer.pdf")
              }
            >
              {translations.landing.footer.disclaimer}
            </p>
            <p
              onClick={() =>
                openInNewTab("https://marketecho.io/cookie_policy.pdf")
              }
            >
              {translations.landing.footer.cookie}
            </p>
          </Text>
          <div>
            <LinkBlock
              onClick={() =>
                openInNewTab("https://www.instagram.com/marketecho.io/")
              }
            >
              <i className="pi pi-instagram" />
            </LinkBlock>

            <LinkBlock onClick={() => openInNewTab("https://t.me/market_echo")}>
              <i className="pi pi-telegram" />
            </LinkBlock>
          </div>
          <div>
            <LinkBlock
              onClick={() => openInNewTab("mailto:support@marketecho.io")}
            >
              <p>
                support@marketecho.io
                <i className="pi pi-envelope" />
              </p>
            </LinkBlock>
          </div>
        </InnerWrap>
        <MobileWrap>
          <div>
            <div>
              <LinkBlock
                onClick={() =>
                  openInNewTab("https://www.instagram.com/marketecho.io/")
                }
              >
                <i className="pi pi-instagram" />
              </LinkBlock>
              <LinkBlock
                onClick={() => openInNewTab("https://t.me/market_echo")}
              >
                <i className="pi pi-telegram" />
              </LinkBlock>
            </div>
            <LinkBlock
              onClick={() => openInNewTab("mailto:support@marketecho.io")}
            >
              <p>
                support@marketecho.io
                <i className="pi pi-envelope" />
              </p>
            </LinkBlock>
          </div>
          <div>
            <Text>
              <p>MarketEcho © 2025</p>
              <p
                onClick={() =>
                  openInNewTab("https://marketecho.io/privacy_policy.pdf")
                }
              >
                {translations.landing.footer.privacy}
              </p>
              <p
                onClick={() =>
                  openInNewTab("https://marketecho.io/terms_and_conditions.pdf")
                }
              >
                {translations.landing.footer.terms}
              </p>
            </Text>
            <Text alignRight>
              <p
                onClick={() =>
                  openInNewTab("https://marketecho.io/disclaimer.pdf")
                }
              >
                {translations.landing.footer.disclaimer}
              </p>
              <p
                onClick={() =>
                  openInNewTab("https://marketecho.io/cookie_policy.pdf")
                }
              >
                {translations.landing.footer.cookie}
              </p>
            </Text>
          </div>
        </MobileWrap>
        <section ref={ref}>
          <img src={greenLogo} alt="Logo" />
        </section>
      </Wrap>
    </AnimateIn>
  );
};

const MobileWrap = styled.div`
  width: 100%;

  > div {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 10px;

    > div {
      display: flex;
      gap: 10px;
    }
  }

  @media screen and (min-width: 751px) {
    display: none;
  }
`;

const Text = styled.div<{ alignRight?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ alignRight }) => alignRight && "flex-end"};

  p {
    color: #afafaf;
    font-size: 12px;
    font-weight: 400;
    cursor: pointer;
    text-align: ${({ alignRight }) => alignRight && "right"};

    &:hover {
      text-decoration: underline;
    }
  }

  @media screen and (max-width: 750px) {
    margin-top: 20px;
  }
`;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #fff;
  width: 100%;
  padding: 36px 36px 0;
  overflow: hidden;
  box-shadow:
    0 16px 32px -4px rgba(12, 12, 13, 0.1),
    0px 4px 4px -4px rgba(12, 12, 13, 0.05);

  section {
    overflow: hidden;
    width: 100%;

    img {
      width: 100%;
      display: block;
      transform: translateY(25%);
    }
  }

  @media screen and (max-width: 750px) {
    padding: 36px 12px 0;
  }
`;

const InnerWrap = styled.div`
  width: 100%;
  max-width: 1440px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  > div {
    display: flex;
    gap: 15px;

    &:nth-of-type(1),
    &:nth-of-type(3) {
      width: 240px;
    }

    &:nth-of-type(3) {
      display: flex;
      justify-content: flex-end;
    }
  }

  @media screen and (max-width: 750px) {
    display: none;
  }
`;

const LinkBlock = styled.div`
  background: #f0f1f3;
  padding: 16px;
  border-radius: 10px;
  width: fit-content;
  cursor: pointer;
  transition: 0.2s;
  display: flex;
  align-items: center;

  > i {
    font-size: 24px;
    color: ${lightTheme.typography.tetriary};
  }
  > p {
    font-weight: 500;
    font-size: 14px;
    color: ${lightTheme.typography.tetriary};
    display: flex;
    align-items: center;

    i {
      margin-left: 10px;
      font-size: 16px;
    }
  }

  &:hover {
    opacity: 0.7;
  }
`;
